import { useCallback, useMemo, useState } from 'react';

import { AccessSourceQuery } from '@ariksa/inventory-core/api';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { selectSecurityGraphNext } from 'containers/Visibility/SecurityGraphNext/selectors';
import { actions } from 'containers/Visibility/SecurityGraphNext/slice';
import { SourceNodeOptions } from 'containers/Visibility/SecurityGraphNext/types';
import {
  groupSourcePage,
  toSourceNodeOptions,
} from 'containers/Visibility/SecurityGraphNext/utils/sourceOptions';

// Pages and searches the entity dropdown on the server while its list is paged.
export const useSourcePages = () => {
  const dispatch = useDispatch();
  const { sourcePaging, accessSources } = useSelector(selectSecurityGraphNext);
  // the last search's counts, for the menu's header and footer
  const [searchMeta, setSearchMeta] = useState<{
    term: string;
    count: number;
    total: number;
    hasMore: boolean;
  } | null>(null);

  const loadNextPage = useCallback(() => {
    if (!sourcePaging?.hasMore || accessSources.isLoading) return;
    const { request, page, size } = sourcePaging;
    dispatch(
      actions.getAccessSources({
        q: { accessSourceQuery: { ...request, page: page + 1, size } },
        onSuccess: res => {
          if (res) dispatch(actions.appendSourcePage({ request, page: res }));
        },
      }),
    );
  }, [accessSources.isLoading, dispatch, sourcePaging]);

  // The first page of matches, across every type. The menu's header and footer
  // show how many matched and, when more did, to type more.
  const searchSources = useMemo(
    () =>
      debounce((term: string, cb: (options: SourceNodeOptions) => void) => {
        if (!sourcePaging) return cb([]);
        const { request, size } = sourcePaging;
        dispatch(
          actions.getAccessSources({
            q: {
              accessSourceQuery: { ...request, search: term, page: 1, size },
            },
            onSuccess: res => {
              setSearchMeta({
                term,
                count: res?.items?.length ?? 0,
                total: res?.total ?? 0,
                hasMore: !!res?.has_more,
              });
              cb(toSourceNodeOptions(groupSourcePage(res?.items)));
            },
            onError: () => cb([]),
          }),
        );
      }, 300),
    [dispatch, sourcePaging],
  );

  // Reloads the list's first page for these accounts; none for every account.
  // Later pages and searches keep them, as they spread the paging request.
  const pickAccounts = useCallback(
    (ids: Array<string | null>) => {
      if (!sourcePaging) return;
      const { source_account_ids, ...base } = sourcePaging.request;
      // the client types the ids loosely; null picks sources without an account
      const request: AccessSourceQuery = ids.length
        ? {
            ...base,
            source_account_ids: ids as AccessSourceQuery['source_account_ids'],
          }
        : base;
      // a search waiting to fire would still use the previous account
      searchSources.cancel();
      dispatch(
        actions.reloadAccessSources({
          q: {
            accessSourceQuery: { ...request, page: 1, size: sourcePaging.size },
          },
          onSuccess: res => {
            if (res)
              dispatch(actions.replaceSourcePage({ request, page: res }));
          },
        }),
      );
    },
    [dispatch, searchSources, sourcePaging],
  );

  return {
    isPaged: !!sourcePaging,
    paging: sourcePaging,
    isLoadingPage: accessSources.isLoading,
    searchMeta,
    loadNextPage,
    searchSources,
    pickAccounts,
  };
};
